import type BadRequestErrorResponse from "@app/config/api/responses/BadRequestErrorResponse";
import { badRequestWithSuggestionResponseSchema } from "@app/config/api/responses/BadRequestErrorResponse";
import { usePostParseDataset } from "@app/queries/datasets";
import type { CSVParseOutput, GenericErrorWithSuggestionResponse } from "@globals";
import { Alert, Box, List, LoadingOverlay, Stack, Text, Transition } from "@mantine/core";
import type { FileWithPath } from "@mantine/dropzone";
import { useEffect, useState } from "react";
import { safeParse } from "valibot";

const DEFAULT_SUGGESTION =
  "Please check that there's at least 2 columns with all its rows with datapoints and try again.";

export default function ParseDatasetView({
  file,
  onComplete
}: {
  file: FileWithPath;
  onComplete: (result: CSVParseOutput) => void;
}) {
  const [parseError, setParseError] = useState<GenericErrorWithSuggestionResponse | null>(null);
  const postParseDataset = usePostParseDataset();

  useEffect(() => {
    const executeParseDataset = async () => {
      const formData = new FormData();
      formData.append("file", file);

      await postParseDataset
        .mutateAsync({ body: formData })
        .then(result => onComplete(result))
        .catch((error: BadRequestErrorResponse) => {
          const result = safeParse(badRequestWithSuggestionResponseSchema, error.json);
          if (result.success) {
            const message: string = result.output.message;
            const suggestion: string = result.output.extra.suggestion;
            setParseError({
              message: message,
              extra: { suggestion }
            });
          } else {
            setParseError({
              message: "An unexpected error ocurred.",
              extra: { suggestion: DEFAULT_SUGGESTION }
            });
          }
        });
    };

    void executeParseDataset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, onComplete]);

  return (
    <Box mih={200}>
      <LoadingOverlay visible={postParseDataset.isPending} />
      <Transition mounted={parseError !== null}>
        {styles => (
          <Alert title="We were unable to parse your CSV file." style={styles}>
            <Text size="sm">Please correct these errors and try again.</Text>
            {parseError && (
              <List my="lg">
                <Stack gap="xs">
                  <Text size="sm">{parseError.message}</Text>
                  {parseError.extra.suggestion && (
                    <Text size="sm" fw={700}>
                      {parseError.extra.suggestion}
                    </Text>
                  )}
                </Stack>
              </List>
            )}
          </Alert>
        )}
      </Transition>
    </Box>
  );
}
